// EditInventory — modal-embedded form for updating an Inventory row.
// Refactored 2026-05-05 (Phase 3.4, Class P) — header onto ModalFormHeader.
// AISC autofill (lookupShape) + 16 fields + 2 toggles preserved.

import React, { useState, useCallback, useRef } from "react";
import CTextField from "../CTextField/CTextField";
import { Button, Chip, Tooltip, Box, Stack, Grid, Typography } from "@mui/material";
import { Inventory2Outlined, CheckCircleOutline as CheckCircleOutlineIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import CSwitch from "../CSwitch/CSwitch";
import { updateInventory } from "../../feature/inventorySlice";
import CSelect from "../CSelect/CSelect";
import { lookupShape } from "../../utils/aiscShapeData";
import { ModalFormHeader, FieldLabel } from "../PaperForm";
import { showErrorToast } from "../../utils/Toast";

function useDebounce(fn, delay) {
  const timer = useRef(null);
  return useCallback(
    (...args) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => fn(...args), delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delay]
  );
}

const EditInventory = (props) => {
  const dispatch = useDispatch();

  const [inventoryData, setInventoryData] = useState({
    id: props.data.id,
    ediStdNomenclature: props.data.ediStdNomenclature,
    aiscManualLabel: props.data.aiscManualLabel,
    weight: props.data.weight,
    depth: props.data.depth,
    grade: props.data.grade,
    poNumber: props.data.poNumber,
    heatNumber: props.data.heatNumber,
    itemType: props.data.itemType,
    lengthReceivedFoot: props.data.lengthReceivedFoot,
    lengthReceivedInch: props.data.lengthReceivedInch,
    quantity: props.data.quantity,
    poPulledFromNumber: props.data.poPulledFromNumber,
    lengthUsedFoot: props.data.lengthUsedFoot,
    lengthUsedInch: props.data.lengthUsedInch,
    lengthRemainingFoot: props.data.lengthRemainingFoot,
    lengthRemainingInch: props.data.lengthRemainingInch,
  });

  const [orderArrivedInFull, setOrderArrivedInFull] = useState(props.data.orderArrivedInFull);
  const [orderArrivedCMTR, setOrderArrivedCMTR] = useState(props.data.orderArrivedCMTR);
  const [selectShape, setSelectedShape] = useState(props.data.shape);
  const [itemType, setItemType] = useState(props.data.itemType);
  const [aiscMatched, setAiscMatched] = useState(false);

  const shapeData = ["2L", "C", "HP", "HSS", "L", "M", "MC", "MT", "PIPE", "PL", "PLT", "S", "ST", "W", "WT"];
  const selectItemType = ["stock", "job"];
  // W-31: CSelect reads item.value and item.name/label, so plain strings gave options with no text
  // and no value. The values are exactly the strings the server accepts (inventoryValidator VALID_SHAPES
  // and itemType "stock" | "job").
  // Edit preselects the stored values; a stored value outside the list is kept as an option so it
  // still shows (and is not silently changed).
  const withStored = (list, stored) =>
    stored && !list.includes(stored) ? [stored, ...list] : list;
  const shapeOptions = withStored(shapeData, props.data.shape).map((s) => ({ value: s, label: s, name: s }));
  const itemTypeOptions = withStored(selectItemType, props.data.itemType).map((s) => ({ value: s, label: s, name: s }));

  const runAiscLookup = useCallback((value) => {
    const match = lookupShape(value);
    if (match) {
      setInventoryData((prev) => ({
        ...prev,
        aiscManualLabel: match.label,
        weight: String(match.weight),
        depth: String(match.d),
      }));
      if (match.shape) setSelectedShape(match.shape);
      setAiscMatched(true);
    } else {
      setAiscMatched(false);
    }
  }, []);

  const debouncedLookup = useDebounce(runAiscLookup, 350);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInventoryData((prev) => ({ ...prev, [name]: value }));

    if (name === "ediStdNomenclature") {
      setAiscMatched(false);
      debouncedLookup(value);
    }
  };

  const handleSubmit = () => {
    if (!selectShape) {
      showErrorToast("Please select a shape.");
      return;
    }
    if (!itemType) {
      showErrorToast("Please select an item type.");
      return;
    }
    const finalData = {
      ...inventoryData,
      orderArrivedCMTR,
      orderArrivedInFull,
      shape: selectShape,
      itemType,
    };
    dispatch(updateInventory(finalData)).then((res) => {
      if (res.type === "updateInventorys/Inventory/fulfilled") {
        props.setOpen(false);
      }
    });
  };

  return (
    <Box sx={{ overflowY: "auto", maxHeight: 550, overflowX: "hidden", pr: 0.5 }}>
      <ModalFormHeader
        overline="SFM · Inventory"
        title="Edit Inventory"
        icon={Inventory2Outlined}
        rightSlot={
          aiscMatched ? (
            <Tooltip title="Shape matched in AISC 16th Edition database — Label, Weight and Depth autofilled">
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label="AISC Matched"
                size="small"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600, letterSpacing: "0.04em" }}
              />
            </Tooltip>
          ) : null
        }
      />

      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="ediStdNomenclature" label="EDI Std Nomenclature"
              defaultValue={inventoryData.ediStdNomenclature}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="aiscManualLabel" label="AISC Manual Label"
              value={inventoryData.aiscManualLabel}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FieldLabel>Shape</FieldLabel>
            <CSelect
              data={shapeOptions} label=""
              initialValue={selectShape} setinitialValue={setSelectedShape}
              margin="0px"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Item Type</FieldLabel>
            <CSelect
              data={itemTypeOptions} label=""
              initialValue={itemType} setinitialValue={setItemType}
              margin="0px"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="weight" label="Weight (lbs/ft)"
              value={inventoryData.weight}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth type="Number" onChange={handleChange}
              name="depth" label="Depth (in)"
              value={inventoryData.depth}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="grade" label="Grade"
              defaultValue={inventoryData.grade}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="poNumber" label="PO Number"
              defaultValue={inventoryData.poNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="heatNumber" label="Heat Number"
              defaultValue={inventoryData.heatNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange}
              name="poPulledFromNumber" label="PO Pulled From Number"
              defaultValue={inventoryData.poPulledFromNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthReceivedFoot" label="Length Received (ft)"
              defaultValue={inventoryData.lengthReceivedFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthReceivedInch" label="Length Received (in)"
              defaultValue={inventoryData.lengthReceivedInch}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthUsedFoot" label="Length Used (ft)"
              defaultValue={inventoryData.lengthUsedFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthUsedInch" label="Length Used (in)"
              defaultValue={inventoryData.lengthUsedInch}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthRemainingFoot" label="Length Remaining (ft)"
              defaultValue={inventoryData.lengthRemainingFoot}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="lengthRemainingInch" label="Length Remaining (in)"
              defaultValue={inventoryData.lengthRemainingInch}
            />
          </Grid>

          <Grid item xs={12}>
            <CTextField
              margin="0px" fullWidth onChange={handleChange} type="Number"
              name="quantity" label="Quantity"
              defaultValue={inventoryData.quantity}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex", flexDirection: { xs: "column", sm: "row" },
            gap: 2, p: 2, border: "1px solid", borderColor: "divider",
            borderRadius: 2, bgcolor: "#fafafa",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Order Arrived In Full
            </Typography>
            <CSwitch
              active={orderArrivedInFull}
              name="orderArrivedInFull"
              setSwitch={() => setOrderArrivedInFull(!orderArrivedInFull)}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Order Arrived CMTR
            </Typography>
            <CSwitch
              active={orderArrivedCMTR}
              name="orderArrivedCMTR"
              setSwitch={() => setOrderArrivedCMTR(!orderArrivedCMTR)}
            />
          </Stack>
        </Box>

        <Button
          disabled={props.isLoading}
          onClick={handleSubmit}
          variant="contained"
          sx={{
            width: "100%",
            mt: 1,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          {props.isLoading ? "Loading…" : "Save changes"}
        </Button>
      </Stack>
    </Box>
  );
};

export default EditInventory;
